// apps/web/src/features/russia-requests/components/RussiaRequestEditor.tsx
//
// #941 editor host. Two surfaces share the same editor shell:
//   - manual create at `/russia-rates/new` (no `:id` param) — empty
//     defaults, complexity from `?kind=`.
//   - draft edit at `/russia-rates/:id/edit` (#1571) — loads the existing
//     DRAFT/RETURN row and prefills the shell so a Manager can complete it.
//
// @aiPurpose Route the russia-rates editor between create and edit mode. The
//   shared `RussiaEditorShell` owns the RHF form, autosave, and drawer; this
//   host only picks the initial state source.

import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router';
import {
  RussiaRequestKind,
  RussiaRequestType,
  type CreateRussiaRequestManualInput,
} from '@magimp/shared';
import { RussiaEditorShell } from './editor/RussiaEditorShell.js';
import { RussiaRequestEditLoader } from './editor/RussiaRequestEditLoader.js';

// WHY (#1545): the create flow reads its complexity from `?kind=`, default
//   SIMPLE. The edit flow derives `kind` from the loaded row instead.
function kindFromParam(raw: string | null): RussiaRequestKind {
  return raw === RussiaRequestKind.COMPLEX ? RussiaRequestKind.COMPLEX : RussiaRequestKind.SIMPLE;
}

export function RussiaRequestEditor() {
  const { id } = useParams<{ id: string }>();
  if (id) return <RussiaRequestEditLoader id={id} />;
  return <RussiaRequestCreateEditor />;
}

function RussiaRequestCreateEditor() {
  const [searchParams] = useSearchParams();
  const kind = kindFromParam(searchParams.get('kind'));

  const defaults = useMemo<CreateRussiaRequestManualInput>(
    () => ({
      type: RussiaRequestType.LEAD,
      kind,
      name: '',
      endAddress: '',
      startCityIds: [],
      // WHY (#1546): seed the COMPLEX request-level totals to null so an
      //   untouched COMPLEX form persists "not set" rather than 0. SIMPLE
      //   forms carry the same null slots verbatim.
      totalWeight: null,
      totalVolume: null,
    }),
    [kind],
  );

  return (
    <RussiaEditorShell kind={kind} defaults={defaults} initialPersistedId={null} editMode={false} />
  );
}
